/**
 * Keeping the timeline's task-set chips current after a delete or a change to a set.
 *
 * The chip reads "k of N". N comes from the page query's task sets (or, for a set created on
 * this page, from the snapshot recordCreatedTaskSet holds), and the Relay updater on the delete
 * mutation lowers it in the store. k does not: the canvas keeps its tasks as plain objects in
 * data.tasks rather than as Relay records, so the updater cannot reach them, and nothing about a
 * change in the store repaints the canvas on its own.
 *
 * It lives in its own module rather than in projects_scheduling_mutation_success.js or
 * canvas_projects_scheduling.js because both import most of the canvas graph, so the rules here
 * can be tested without loading it or standing up the page component.
 */

import {shiftedPosition} from '../../../../forecast-app/project-tab/projects/scoping-page/task-set/taskSetDeleteRenumber';

/** The fields of a canvas task this module reads, and the one it writes. */
export interface CanvasTaskSetTask {
	id: string;
	taskSetId?: string | null;
	taskSetSequence?: number | null;
}

/** A set recorded on the page component by recordCreatedTaskSet. */
export interface CreatedTaskSetSnapshot {
	id: string;
	instanceCount?: number | null;
}

/**
 * Closes the gap a deleted task leaves in its set, on the canvas's own task objects.
 *
 * Every surviving task of the same set moves down by one if it sat after the deleted one, the
 * same derivation the server makes. A deleted task with no known position shifts nothing, since
 * there is nothing to measure against, but it still left the set one task smaller.
 *
 * The total for a set the page query lists is lowered in the store by the delete updater, and
 * the chip index rebuilds when those edges change. A set created on this page is not in those
 * edges: its total is the snapshot in `createdTaskSets`, which nothing else updates, so it is
 * lowered here. The snapshot is replaced with a copy rather than edited, because it is the
 * mutation response, which Relay may have frozen.
 *
 * @returns true when a snapshot was lowered, so the caller must mark the chip index stale; the
 * k values need no such flag, since the bars read them from these objects on every paint.
 */
export const renumberCanvasAfterDelete = (
	tasks: CanvasTaskSetTask[],
	createdTaskSets: Map<string, CreatedTaskSetSnapshot> | null | undefined,
	removed: CanvasTaskSetTask | null | undefined
): boolean => {
	const setId = removed?.taskSetId;
	if (!removed || !setId) {
		return false;
	}

	const removedPosition = removed.taskSetSequence;
	if (removedPosition != null) {
		tasks.forEach(task => {
			if (task.id === removed.id || task.taskSetId !== setId || task.taskSetSequence == null) {
				return;
			}
			task.taskSetSequence = shiftedPosition(task.taskSetSequence, [removedPosition]);
		});
	}

	const snapshot = createdTaskSets?.get(setId);
	if (!createdTaskSets || !snapshot || typeof snapshot.instanceCount !== 'number') {
		return false;
	}
	createdTaskSets.set(setId, {...snapshot, instanceCount: Math.max(0, snapshot.instanceCount - 1)});
	return true;
};

interface TaskSetsProps {
	viewer?: {project?: {taskSets?: {edges?: unknown} | null} | null} | null;
}

/**
 * Whether the page's task sets changed in a way the canvas should repaint for.
 *
 * Relay hands the page a new `edges` array whenever a field it selects on any set changes, such
 * as a delete lowering `instanceCount` or the drawer's refetch after a set is shortened. The
 * canvas does not re-render with its props, so without a redraw the chips keep the old total
 * until something else paints.
 *
 * Before the first full load the load's own redraw paints the chips, so nothing is asked for.
 */
export const shouldRedrawForTaskSets = (
	prevProps: TaskSetsProps | null | undefined,
	props: TaskSetsProps | null | undefined,
	dataLoaded: boolean | null | undefined
): boolean => {
	const edges = props?.viewer?.project?.taskSets?.edges;
	return !!dataLoaded && !!edges && edges !== prevProps?.viewer?.project?.taskSets?.edges;
};

export interface FrameScheduler {
	/** Runs the callback on the next animation frame. A second call before then reuses the first. */
	schedule: () => void;
	/** Drops a pending run, if there is one. */
	cancel: () => void;
}

/** At most one pending run of `callback`, on the next animation frame. */
export const createFrameScheduler = (callback: () => void): FrameScheduler => {
	let frame: number | null = null;
	return {
		schedule: () => {
			if (frame != null) {
				return;
			}
			frame = requestAnimationFrame(() => {
				frame = null;
				callback();
			});
		},
		cancel: () => {
			if (frame == null) {
				return;
			}
			cancelAnimationFrame(frame);
			frame = null;
		},
	};
};
